import React, { useEffect, useState } from "react";
import { api_Routes } from "../../../tools/api_Routes";
import { Helper } from "../../../tools/Helper";
import {
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TablePagination,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
} from "@mui/material";
import DeleteModal from "../DeleteModal";
import { useDispatch } from "react-redux";
import { setOpen } from "../../../Redux/ModalReducer";
import DataTable from "../data-table";

const TrustedPanelsTable = () => {
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [selectedId, setSelectedId] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);

  const handleClose = () => {
    setShow(false);
    dispatch(setOpen(false));
  };

  const handleShow = () => {
    setShow(true);
    dispatch(setOpen(true));
  };

  // Filter states
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [readingStatus, setReadingStatus] = useState(null);
  const [readingType, setReadingType] = useState(null);
  const [expired, setExpired] = useState(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const handleKeywordChange = (event) => {
    setKeyword(event.target.value);
  };
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedKeyword(keyword);
      if (keyword) {
        setFiltersChanged(true);
      }
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [keyword]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const getData = async () => {
    setIsLoading(true);
    // Build the query string dynamically
    const queryParams = [];
    if (debouncedKeyword) queryParams.push(`url=${debouncedKeyword}`);
    if (readingStatus) queryParams.push(`reading_status=${readingStatus}`);
    if (readingType) queryParams.push(`reading_type=${readingType}`);
    if (expired !== null) queryParams.push(`expired=${expired}`);

    const queryString = queryParams.length ? `${queryParams.join("&")}` : "";

    const { response, message } = await Helper.Get({
      url: `${api_Routes.Panels.View}?per_page=${rowsPerPage}&page=${
        currentPage + 1
      }&trust=1${queryString?.length > 0 ? `&${queryString}` : ""}`,
      hasToken: true,
    });
    if (response) {
      setData(response);
    } else {
      setData([]);
    }
    setIsLoading(false);
  };

  // Effect for filter changes
  useEffect(() => {
    if (filtersChanged || shouldRefresh) {
      setCurrentPage(0);
      setFiltersChanged(false);
      setShouldRefresh(false);
    } else {
      getData();
    }
  }, [filtersChanged, currentPage, shouldRefresh, rowsPerPage]);

  const handleResetFilters = () => {
    setReadingStatus(null);
    setReadingType(null);
    setExpired(null);
    setCurrentPage(0);
    setFiltersChanged(true);
  };

  return (
    <div>
      {/* Filter Section */}
      <div className="flex justify-between items-center flex-col gap-4 mb-3">
        <label className="font-bold inline-block text-textColor dark:text-darkTextColor text-[15px]  text-start">
          Search by url :
          <input
            placeholder="Write any keyword"
            type="text"
            name="keyword"
            value={keyword}
            onChange={handleKeywordChange}
            autoComplete="off"
            className="duration-200 font-normal bg-secondary dark:bg-darkPrimary text-textColor dark:text-darkTextColor ml-2
                           border-[1px] input-box 
                           rounded-[0.25rem]  py-1 px-3 outline-none"
          />
        </label>
        <div className="grid grid-cols-1 lg:grid-cols-3 items-center w-full gap-4">
          {/* Expired Filter */}
          <FormControl className="w-full">
            <InputLabel id="expired-select-label">Expired</InputLabel>
            <Select
              labelId="expired-select-label"
              id="expired-select"
              value={expired}
              onChange={(e) => {
                setExpired(e.target.value);
                setFiltersChanged(true);
              }}
              label="Expired"
              sx={{ textAlign: "start" }}
            >
              <MenuItem value={null}>All</MenuItem>
              <MenuItem value={1}>Expired</MenuItem>
              <MenuItem value={0}>Valid</MenuItem>
            </Select>
          </FormControl>

          {/* Reading Status Filter */}
          <FormControl className="w-full">
            <InputLabel id="reading-status-select-label">
              Reading Status
            </InputLabel>
            <Select
              labelId="reading-status-select-label"
              id="reading-status-select"
              value={readingStatus}
              onChange={(e) => {
                setReadingStatus(e.target.value);
                setFiltersChanged(true);
              }}
              label="Reading Status"
              sx={{ textAlign: "start" }}
            >
              <MenuItem value={null}>All</MenuItem>
              <MenuItem value="not_read">Not Read</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="success">Success</MenuItem>
            </Select>
          </FormControl>

          {/* Reading Type Filter */}
          <FormControl className="w-full">
            <InputLabel id="reading-type-select-label">Reading Type</InputLabel>
            <Select
              labelId="reading-type-select-label"
              id="reading-type-select"
              value={readingType}
              onChange={(e) => {
                setReadingType(e.target.value);
                setFiltersChanged(true);
              }}
              label="Reading Type"
              sx={{ textAlign: "start" }}
            >
              <MenuItem value={null}>All</MenuItem>
              <MenuItem value="API">API</MenuItem>
              <MenuItem value="HTML">HTML</MenuItem>
            </Select>
          </FormControl>
        </div>
        {/* Reset Button */}
        <button
          className="bg-gray-200 text-black px-4 py-2 rounded"
          onClick={handleResetFilters}
          disabled={
            readingStatus === null && readingType === null && expired === null
          }
        >
          Reset Filters
        </button>
      </div>

      {/* Table and Pagination */}
      {isLoading ? (
        <LinearProgress sx={{ marginTop: 1 }} />
      ) : data && data?.data?.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <DataTable
              data={data?.data}
              setSelectedId={setSelectedId}
              handleShow={handleShow}
            />
          </div>
          <div className="mt-4 flex gap-4 items-center justify-center">
            {/* Pagination */}
            <TablePagination
              page={currentPage}
              className="mt-auto"
              onPageChange={handlePageChange}
              count={data.pagination.total}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              variant="outlined"
              shape="rounded"
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
            {/* Navigate to Last Page Button */}
            <button
              className="bg-primary text-white px-4 py-2 mt-2 rounded"
              onClick={() =>
                setCurrentPage(
                  currentPage ===
                    Math.ceil(data.pagination.total / rowsPerPage) - 1
                    ? 0 // Navigate to the first page
                    : Math.ceil(data.pagination.total / rowsPerPage) - 1 // Navigate to the last page
                )
              }
              disabled={data.pagination.total <= rowsPerPage}
            >
              {currentPage ===
              Math.ceil(data.pagination.total / rowsPerPage) - 1
                ? "Go to First Page"
                : "Go to Last Page"}{" "}
            </button>
          </div>
          <DeleteModal
            show={show}
            handleClose={handleClose}
            selectedId={selectedId}
            setShouldRefresh={setShouldRefresh}
          />
        </>
      ) : (
        <TableContainer>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  There are no data yet
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default TrustedPanelsTable;
